import React from 'react';
import Link from 'next/link';

export const RecentSection: React.FC = () => {
  return (
    <section id="updates" className="flex flex-col gap-6 scroll-mt-8">
      <h2 className="text-3xl font-bold border-b border-zinc-700 pb-4">Updates</h2>

      {/* Version 1.6.0 */}
      <div className="border border-zinc-700 rounded-lg p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
            v1.6.0
          </span>
          <span className="text-zinc-400 text-sm">September 25, 2025</span>
        </div>
        <h3 className="text-xl font-semibold mb-3">Community Engagement & Platform Maturity</h3>
        <div className="space-y-3 text-zinc-400">
          <p>
            Social features, creator verification and leaderboard, backend performance improvements,
            and platform maturity updates.
          </p>

          <div className="space-y-2">
            <h4 className="text-white font-medium">👑 Creator Verification & Recognition</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Verified profiles for top creators and hardware manufacturer partners</li>
              <li>
                New creator{' '}
                <Link
                  href="/top-creators"
                  className="text-white hover:text-gray-300 underline"
                  target="_blank"
                >
                  leaderboard
                </Link>{' '}
                page showcasing top creators
              </li>
              <li>Featured creators highlighted on homepage</li>
            </ul>
          </div>

          <div className="space-y-2">
            <h4 className="text-white font-medium">💬 Social Features & Community Interaction</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Comments and replies system for tone discussions</li>
              <li>Up/down voting on comments</li>
              <li>Report comments function with integrated moderation tools</li>
              <li>
                Real-time notifications for social interactions available at{' '}
                <Link
                  href="/notifications"
                  className="text-white hover:text-gray-300 underline"
                  target="_blank"
                >
                  tone3000.com/notifications
                </Link>
              </li>
              <li>
                Email notifications for social interactios. You can now choose which notifications
                you want to receive via email via{' '}
                <Link
                  href="/settings"
                  className="text-white hover:text-gray-300 underline"
                  target="_blank"
                >
                  tone3000.com/settings
                </Link>{' '}
              </li>
              <li>
                New TONE3000 social accounts for TikTok, Instagram, and YouTube with backlinks on
                the site
              </li>
            </ul>
          </div>

          <div className="space-y-2">
            <h4 className="text-white font-medium">⚡ Performance & Technical Improvements</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Concurrent model and IR uploads for faster processing</li>
              <li>
                Backend performance overhaul: migrated from PostgreSQL triggers to cron-based
                materialized view updates for significantly improved creator CRUD performance
              </li>
              <li>Structured JSON-LD data implementation for better search engine indexing</li>
              <li>Fixed layout shifts caused by server-client mismatches</li>
              <li>Simplified canonical query string handling</li>
              <li>Increased maximum parameters allowed for custom architecture training</li>
            </ul>
          </div>

          <div className="space-y-2">
            <h4 className="text-white font-medium">🛡️ Platform Protection & Content Quality</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>
                Updated terms and privacy policies to discourage unauthorized model scraping and
                republishing with content protection for creator intellectual property
              </li>
              <li>New monitoring and ban tooling to detect and prevent bot behavior</li>
              <li>Removed thin UGC pages from sitemap with noindex tags to avoid soft 404s</li>
            </ul>
          </div>

          <div className="space-y-2">
            <h4 className="text-white font-medium">♿ Accessibility & User Experience</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Keyboard navigation improvements throughout the platform</li>
              <li>Enhanced screen reader compatibility and accessibility</li>
              <li>Improved tone card semantic structure following web best practices</li>
              <li>Added important FAQs to homepage for better user onboarding</li>
              <li>
                Helpful modal when downloading amp-head only models to steer users to pair with IRs
              </li>
              <li>
                IR length limited to maximum 60 seconds to prevent users mistakenly uploading sweep
                files
              </li>
            </ul>
          </div>

          <div className="space-y-2">
            <h4 className="text-white font-medium">🔍 Search & Navigation Improvements</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>
                Refactored search URL parameters to use semantic, readable values - tags and makes
                now use descriptive parameters (e.g., ?tags=metal instead of ?tags=1234) for
                cleaner, more intuitive URLs
              </li>
            </ul>
          </div>

          <div className="space-y-2">
            <h4 className="text-white font-medium">🔌 API Updates</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>
                New{' '}
                <Link
                  href="/api#users"
                  className="text-white hover:text-gray-300 underline"
                  target="_blank"
                >
                  /api/v1/users
                </Link>{' '}
                endpoint for retrieving user with public content, sorting by most tones, downloads,
                favorites, or models, plus username text search functionality
              </li>
              <li>
                Deprecation of camelCase parameters in favor of snake_case (backwards compatible).
                Updated API documentation available at{' '}
                <Link
                  href="/api"
                  className="text-white hover:text-gray-300 underline"
                  target="_blank"
                >
                  tone3000.com/api
                </Link>
                . Developers encouraged to migrate to new parameter format
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Version 1.5.0 */}
      <div className="border border-zinc-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
            v1.5.0
          </span>
          <span className="text-zinc-400 text-sm">July 18, 2025</span>
        </div>
        <h3 className="text-xl font-semibold mb-3">Trust Building & Community Growth</h3>
        <div className="space-y-3 text-zinc-400">
          <p>
            Community has reached 1.5M downloads and 100K models. New requirements and features to
            build trust within the TONE3000 community.
          </p>
          <div className="space-y-2">
            <h4 className="text-white font-medium">🔒 Trust & Quality Requirements</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Photo requirements: Only unedited photos of actual gear used during capture</li>
              <li>
                Detailed info: 100+ character descriptions, photo, tags, and makes/models required
                for homepage featuring
              </li>
              <li>
                Enhanced creator flows with helpful instructions, examples, and guidance for
                high-quality tone postings
              </li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-white font-medium">⚡ New Features & Improvements</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Batch training now supports dry-wet processing</li>
              <li>Multiple YouTube embeds allowed on tone pages</li>
              <li>Improved login flow with magic link or OTP code options</li>
              <li>Performance improvements for tone pages with hundreds of models</li>
              <li>Preview player performance improvements</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-white font-medium">🎛️ Preview Player Enhancements</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>New DI/inputs from community: bass, metal guitar</li>
              <li>Added bass IRs and bass NAMs for preview player</li>
              <li>All preview player options now open source and community-contributable</li>
              <li>
                Available on{' '}
                <a
                  href="https://github.com/tone-3000/neural-amp-modeler-wasm/tree/main/ui/public"
                  className="text-white hover:text-gray-300 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GitHub
                </a>{' '}
                for contributions
              </li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-white font-medium">🐛 Bug Fixes</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Fixed HEIC image upload issues</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Version 1.4.0 */}
      <div className="border border-zinc-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
            v1.4.0
          </span>
          <span className="text-zinc-400 text-sm">June 19, 2025</span>
        </div>
        <h3 className="text-xl font-semibold mb-3">TONE3000 API Beta Launch</h3>
        <div className="space-y-3 text-zinc-400">
          <p>
            Version one of the TONE3000 API is now available for developers and hardware
            manufacturers to access the TONE3000 library of NAM profiles and impulse responses.
          </p>
          <div className="space-y-2">
            <h4 className="text-white font-medium">🔌 API Features</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Direct access to TONE3000 tones without manual downloads</li>
              <li>User authentication to access created or favorited tones</li>
              <li>API key management and usage tracking</li>
              <li>
                Open-source demo web app available on{' '}
                <a
                  href="https://github.com/tone-3000/api"
                  className="text-white hover:text-gray-300 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GitHub
                </a>
              </li>
            </ul>
            <p className="text-sm mt-3">
              📚{' '}
              <Link
                href="/api"
                className="text-white hover:text-gray-300 underline"
                target="_blank"
              >
                Complete API documentation
              </Link>{' '}
              available at www.tone3000.com/api
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="text-white font-medium">🎛️ Use Cases</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Pedals that load amp models directly</li>
              <li>Block-based signal chain builders</li>
              <li>DAW plugins with instant pedal access</li>
              <li>Convolution reverbs pulling IRs from TONE3000</li>
              <li>Synths with TONE3000-powered saturation</li>
              <li>Hardware displays showing tone download counts</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Version 1.3.0 */}
      <div className="border border-zinc-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
            v1.3.0
          </span>
          <span className="text-zinc-400 text-sm">June 6, 2025</span>
        </div>
        <h3 className="text-xl font-semibold mb-3">Neural Amp Modeler WASM Open Source Release</h3>
        <div className="space-y-3 text-zinc-400">
          <p>
            TONE3000 has open sourced our web preview player technology, releasing Neural Amp
            Modeler WASM - an open source fork of Steve Atkinson's NAM Core DSP library for
            real-time audio modeling in web browsers.
          </p>
          <div className="space-y-2">
            <h4 className="text-white font-medium">🔧 Development Tools</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Complete build tools to compile core DSP library to WASM</li>
              <li>Ready-to-use React component for playing NAM models and IRs</li>
              <li>Full documentation and examples</li>
              <li>
                Available on{' '}
                <a
                  href="https://github.com/tone-3000/neural-amp-modeler-wasm"
                  className="text-white hover:text-gray-300 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GitHub
                </a>{' '}
                and{' '}
                <a
                  href="https://www.npmjs.com/package/neural-amp-modeler-wasm"
                  className="text-white hover:text-gray-300 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  NPM
                </a>
              </li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-white font-medium">🎯 Use Cases</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Integrate audio modeling into web applications</li>
              <li>Run neural networks directly in the browser</li>
              <li>Build custom audio processing tools</li>
              <li>Create interactive tone preview systems</li>
            </ul>
          </div>
          <p className="text-sm mt-3">
            🙏 Special thanks to{' '}
            <a
              href="https://github.com/Kutalia/NeuralAmpModelerCore_WASM"
              className="text-white hover:text-gray-300 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Kutalia's previous work
            </a>{' '}
            on NeuralAmpModelerCore_WASM
          </p>
        </div>
      </div>

      {/* Version 1.2.0 */}
      <div className="border border-zinc-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
            v1.2.0
          </span>
          <span className="text-zinc-400 text-sm">May 2, 2025</span>
        </div>
        <h3 className="text-xl font-semibold mb-3">Batch Training Feature</h3>
        <div className="space-y-3 text-zinc-400">
          <p>
            Batch training is now available, allowing users to upload multiple sweep recordings and
            submit batch training all at once.
          </p>
          <div className="space-y-2">
            <h4 className="text-white font-medium">⚡ New Capabilities</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Upload multiple sweep recordings simultaneously</li>
              <li>Submit batch training jobs in a single operation</li>
              <li>Streamlined workflow for processing multiple gear captures</li>
              <li>Improved efficiency for bulk model creation</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Version 1.1.0 */}
      <div className="border border-zinc-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
            v1.1.0
          </span>
          <span className="text-zinc-400 text-sm">March 21, 2025</span>
        </div>
        <h3 className="text-xl font-semibold mb-3">Community Milestones & Feature Updates</h3>
        <div className="space-y-3 text-zinc-400">
          <p>
            One week after launch, the community has reached incredible milestones: 50K models
            created and 500K downloads. New features and improvements have been added based on user
            feedback.
          </p>
          <div className="space-y-2">
            <h4 className="text-white font-medium">🔍 Enhanced Search & Discovery</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Searchable filters for makes, models, and tags (e.g., "metal")</li>
              <li>New "Creator" filter to find tones from favorite creators</li>
              <li>Creator filter doubles as a mini leaderboard</li>
              <li>New "Trending" algorithm balancing downloads and favorites</li>
              <li>Improved promotion of newer tones for better equity</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-white font-medium">🎛️ Improved User Experience</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Smarter preview player remembers last-used input, IR, and NAM</li>
              <li>Enhanced image upload supporting .heic, .webp and other formats</li>
              <li>Auto compression eliminates size limits for image uploads</li>
              <li>Numerous bug fixes and UI/UX improvements</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Version 1.0.0 */}
      <div className="border border-zinc-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="bg-orange-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
            v1.0.0
          </span>
          <span className="text-zinc-400 text-sm">March 13, 2025</span>
        </div>
        <h3 className="text-xl font-semibold mb-3">TONE3000 Beta Launch</h3>
        <div className="space-y-3 text-zinc-400">
          <p>
            ToneHunt and TONEZONE3000 have been unified into a single platform: TONE3000. This
            complete platform rebuild combines community sharing and gear capture in one open-source
            Neural Amp Modeling ecosystem.
          </p>
          <div className="space-y-2">
            <h4 className="text-white font-medium">🎉 Major New Features</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Add images to your tone packs for better visual organization</li>
              <li>Edit, delete, download, and move individual models within tone packs</li>
              <li>Add links to your captures and profile to promote yourself</li>
              <li>
                Advanced search across tone packs, makes, descriptions, tags, platforms, and
                individual models
              </li>
              <li>
                Filter by NAM architecture/size and models calibrated with input/output levels
              </li>
              <li>Train models on TONE3000 with private or public sharing options</li>
              <li>Search existing tags or create your own when capturing/uploading models</li>
              <li>Stats on your shared tones including total downloads and favorites</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-white font-medium">🚀 Performance & UX</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Website benchmarked 30x faster than previous versions</li>
              <li>
                Full redesign across all pages (landing, profile, tone page, account, search,
                capture/upload)
              </li>
              <li>New "preview player" on every tone page with DI and IR options</li>
              <li>Works seamlessly on all modern browsers, iOS, and Android</li>
              <li>Preview individual models in tone packs with various NAM models</li>
              <li>IR tone packs support previewing each IR with different NAM models</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-white font-medium">🔄 Migration & Compatibility</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>Seamless login with existing ToneHunt or TONEZONE3000 credentials</li>
              <li>All TONEZONE3000 models migrated and organized into tone packs</li>
              <li>
                ToneHunt and TONEZONE3000 links automatically redirect to correct TONE3000 pages
              </li>
              <li>Complete content migration preserving all user data and contributions</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="text-white font-medium">🔮 Coming Soon</h4>
            <ul className="list-disc space-y-1 ml-4">
              <li>API for plugin developers and gear manufacturers</li>
              <li>Pro-tier gear capture including batch uploads</li>
              <li>TONE3000 plugin for direct DAW integration</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};
